import { Message, Response, SyncMessage, ResponseCallback, ErrorResponse } from '@augloop/session-protocol';
import { SchemaObject } from '@augloop/types-core';
import * as Deque from 'double-ended-queue';
import { LogCountLimiter, Utils } from '../utils';
import { Operation as LogOperation, Log, LogCategory } from '@augloop/logging/dist/src/client/logging';

export interface IMessageQueue {
    /**
     * Pushes message and it's onResponse callback into queue
     */
    push(message: Message | Response | Uint8Array, onResponse?: ResponseCallback, attemptNumber?: number);

    /**
     * Sends queued messages one by one while conditions for sending next message are satisfied.
     */
    sendOnSessionInitialized(shouldDropSequencedSyncMessages: boolean): void;
}

export enum ClientSendMessageErrorReason {
    ArrivedBeforeReseeding = 'Message is dropped from queue since it arrived before reseeding is started',
    DroppedAsOldestInQueue = 'Message is dropped as oldest in full queue'
}

export type IMessageQueueCallbacks = {
    sendMessage: (message: Message, onResponse?: ResponseCallback, attemptNumber?: number) => void;
    sendBytes: (buffer: Uint8Array) => void;
    canSendMessage: () => boolean;
    dropOnlySequencedSyncsOnReseed: boolean; // ChangeGate, can be removed once sufficiently baked
}

type QueueItem = {
    message: Message | Response | Uint8Array;
    timeQueued: number;
    onResponse?: ResponseCallback;
    attemptNumber?: number;
}

// Max number of messages we can store in queue
const queueMaxSize: number = 1000;

export class MessageQueue implements IMessageQueue {
    public readonly logOp: LogOperation = new LogOperation({ operationName: 'MessageQueue', success: true });
    private readonly callbacks: IMessageQueueCallbacks;
    private readonly messageQueueLogCountLimiter = new LogCountLimiter('MessageQueue');

    private queue: Deque<QueueItem> = new Deque(queueMaxSize);

    public constructor(
        callbacks: IMessageQueueCallbacks
    ) {
        this.callbacks = callbacks;
    }

    public push(message: Message | Response | Uint8Array, onResponse?: ResponseCallback, attemptNumber?: number): void {
        const queue = this.queue;

        if (queue.length === queueMaxSize) {
            this.messageQueueLogCountLimiter.log(() => {
                this.logOp.resourceId = 'QueueFull';
                this.logOp.durationMs = 0;
                Log.warn(0x222a269d /* tag_8k803 */, LogCategory.CoreDefault, this.logOp);
            });

            // Remove oldest
            const item = queue.shift();
            if (item.onResponse) {
                item.onResponse(new ErrorResponse({ error: ClientSendMessageErrorReason.DroppedAsOldestInQueue }));
            }
        }

        queue.push({ message: message, onResponse: onResponse, timeQueued: Utils.getCurrentTimeMs(), attemptNumber });
    }

    public sendOnSessionInitialized(shouldDropSequencedSyncMessages: boolean): void {
        const queueSize = this.queue.length;
        let droppedSyncMessages = 0;
        if (queueSize > 0) {
            const oldestMessageInQueueTimeWaitingMs = Utils.getCurrentTimeMs() - this.queue.get(0).timeQueued;
            while (this.queue.length > 0 && this.callbacks.canSendMessage()) {
                const item = this.queue.shift();

                // Note, the reason we only drop sequenced SyncMessages is because those are the ones that will be
                // inconsistent with the session state after reseeding (and redundant with the seed content anyway),
                // and should therefore be discarded. There are some SyncMessages that are unsequenced (e.g. the ones
                // containing SignalOperations) and those should be preserved across reseed.
                if (shouldDropSequencedSyncMessages && item.message instanceof SyncMessage && SchemaObject.matchesTypesFor(item.message, [SyncMessage.getTypeName()]) && (!this.callbacks.dropOnlySequencedSyncsOnReseed || (item.message as SyncMessage).seq >= 0)) {
                    droppedSyncMessages++;
                    if (item.onResponse) {
                        item.onResponse(new ErrorResponse({ error: ClientSendMessageErrorReason.ArrivedBeforeReseeding }));
                    }
                } else if (item.message instanceof Uint8Array) {
                    this.callbacks.sendBytes(item.message as Uint8Array);
                } else {
                    this.callbacks.sendMessage(item.message, item.onResponse, item.attemptNumber);
                }
            }

            this.messageQueueLogCountLimiter.log(() => {
                this.logOp.resourceId = 'SendOnSessionInitialized';
                this.logOp.resultDescription = `Queue size before: ${queueSize}, after: ${this.queue.length}. droppedSyncMessages: ${droppedSyncMessages}`;
                this.logOp.durationMs = oldestMessageInQueueTimeWaitingMs;
                Log.warn(0x222a269e /* tag_8k804 */, LogCategory.CoreDefault, this.logOp);
            });
        }
    }
}
